import React, { useState, useEffect } from 'react';
import { Edit, X, Check } from 'lucide-react';

const LeaderboardManager = ({ apiUrl, token, currentUser }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [myStats, setMyStats] = useState(null);
  const [globalStats, setGlobalStats] = useState(null);
  const [pointsConfig, setPointsConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('leaderboard');
  const [editingConfigId, setEditingConfigId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const isAdmin = currentUser?.role === 'admin';

  const authenticatedFetch = async (url, options = {}) => {
    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [leaderboardRes, statsRes, globalRes, configRes] = await Promise.all([
        authenticatedFetch(`${apiUrl}/points/leaderboard?limit=50`),
        authenticatedFetch(`${apiUrl}/points/my-stats`),
        authenticatedFetch(`${apiUrl}/points/stats`),
        authenticatedFetch(`${apiUrl}/points/config`)
      ]);

      const leaderboardData = await leaderboardRes.json();
      const statsData = await statsRes.json();
      const globalData = await globalRes.json();
      const configData = await configRes.json();

      setLeaderboard(leaderboardData);
      setMyStats(statsData);
      setGlobalStats(globalData);
      setPointsConfig(configData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const getRankEmoji = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const getCategoryColor = (category) => {
    const colors = {
      essential: '#3b82f6',
      complementary: '#10b981',
      seo: '#f59e0b',
      avis: '#8b5cf6'
    };
    return colors[category] || '#6b7280';
  };

  const startEdit = (config) => {
    setEditingConfigId(config._id);
    setEditValue(config.points.toString());
  };

  const cancelEdit = () => {
    setEditingConfigId(null);
    setEditValue('');
  };

  const saveEdit = async (configId) => {
    if (!editValue || isNaN(editValue) || parseInt(editValue) < 0) {
      alert('Veuillez entrer un nombre valide (≥ 0)');
      return;
    }

    setSaving(true);
    try {
      const response = await authenticatedFetch(`${apiUrl}/points/config/${configId}`, {
        method: 'PUT',
        body: JSON.stringify({
          points: parseInt(editValue)
        })
      });

      if (response.ok) {
        const updatedConfig = await response.json();
        
        // Mettre à jour localement
        setPointsConfig(pointsConfig.map(c => 
          c._id === configId ? updatedConfig : c
        ));
        
        setEditingConfigId(null);
        setEditValue('');
        alert('✅ Points mis à jour avec succès');
      } else {
        const error = await response.json();
        alert(`❌ Erreur: ${error.error}`);
      }
    } catch (error) {
      console.error('Error saving config:', error);
      alert('❌ Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        Chargement...
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '10px', 
        marginBottom: '24px',
        borderBottom: '2px solid #e5e7eb'
      }}>
        <button
          onClick={() => setActiveTab('leaderboard')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'leaderboard' ? '3px solid #3b82f6' : 'none',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            color: activeTab === 'leaderboard' ? '#3b82f6' : '#6b7280'
          }}
        >
          🏆 Classement
        </button>
        <button
          onClick={() => setActiveTab('mystats')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'mystats' ? '3px solid #3b82f6' : 'none',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            color: activeTab === 'mystats' ? '#3b82f6' : '#6b7280'
          }}
        >
          📊 Mes Stats
        </button>
        <button
          onClick={() => setActiveTab('config')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'config' ? '3px solid #3b82f6' : 'none',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            color: activeTab === 'config' ? '#3b82f6' : '#6b7280'
          }}
        >
          ⚙️ Configuration
        </button>
      </div>

      {/* Global Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
            Contributeurs actifs
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#111827' }}>
            {globalStats?.totalUsers || 0}
          </div>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
            Points distribués
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#f59e0b' }}>
            {globalStats?.totalPoints || 0}
          </div>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
            Éditions validées
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#10b981' }}>
            {globalStats?.totalValidatedEdits || 0}
          </div>
        </div>
        {globalStats?.topEditor && (
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
              🏆 Top Contributeur
            </div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#111827' }}>
              {globalStats.topEditor.name}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>
              {globalStats.topEditor.points} pts
            </div>
          </div>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'leaderboard' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <h2 style={{ fontSize: '18px', color: '#111827', margin: 0 }}>
              🏆 Classement des Contributeurs
            </h2>
          </div>
          <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {leaderboard.map((user, index) => (
              <div
                key={user.email}
                style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid #f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  backgroundColor: index < 3 ? '#fef3c7' : 'white'
                }}
              >
                <div style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  minWidth: '50px',
                  textAlign: 'center'
                }}>
                  {getRankEmoji(user.rank)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#111827',
                    marginBottom: '4px'
                  }}>
                    {user.name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    {user.email}
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#6b7280'
                  }}>
                    <span>✅ {user.validatedEdits} validées</span>
                    <span>⏳ {user.pendingEdits} en attente</span>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    fontSize: '28px',
                    fontWeight: '700',
                    color: '#f59e0b'
                  }}>
                    {user.totalPoints}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    points
                  </div>
                  {user.pendingPoints > 0 && (
                    <div style={{
                      fontSize: '12px',
                      color: '#3b82f6',
                      marginTop: '4px'
                    }}>
                      +{user.pendingPoints} en attente
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'mystats' && myStats && (
        <div>
          {/* Ma carte personnelle */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '16px',
            padding: '32px',
            color: 'white',
            marginBottom: '24px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '24px'
            }}>
              <div>
                <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
                  Mon Classement
                </div>
                <div style={{ fontSize: '48px', fontWeight: '700' }}>
                  {getRankEmoji(myStats.rank)}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
                  Total Points
                </div>
                <div style={{ fontSize: '48px', fontWeight: '700' }}>
                  {myStats.points.total}
                </div>
              </div>
            </div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '16px'
            }}>
              <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                padding: '16px',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>
                  En attente
                </div>
                <div style={{ fontSize: '24px', fontWeight: '700' }}>
                  +{myStats.points.pending}
                </div>
              </div>
              <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                padding: '16px',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>
                  Validées
                </div>
                <div style={{ fontSize: '24px', fontWeight: '700' }}>
                  {myStats.stats.validatedEdits}
                </div>
              </div>
              <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                padding: '16px',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>
                  En cours
                </div>
                <div style={{ fontSize: '24px', fontWeight: '700' }}>
                  {myStats.stats.pendingEdits}
                </div>
              </div>
            </div>
          </div>

          {/* Historique récent */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h3 style={{ fontSize: '18px', color: '#111827', margin: 0 }}>
                📝 Mes Dernières Contributions
              </h3>
            </div>
            <div style={{ padding: '20px' }}>
              {myStats.recentEdits.length > 0 ? (
                myStats.recentEdits.map((edit, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: '16px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      marginBottom: '12px',
                      borderLeft: '4px solid #10b981'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '8px'
                    }}>
                      <div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#111827',
                          marginBottom: '4px'
                        }}>
                          {edit.shopName}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          Champ modifié: {edit.field}
                        </div>
                      </div>
                      <div style={{
                        fontSize: '20px',
                        fontWeight: '700',
                        color: '#10b981'
                      }}>
                        +{edit.pointsEarned}
                      </div>
                    </div>
                    <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                      Validé le {new Date(edit.validatedAt).toLocaleDateString('fr-FR')} par {edit.validatedBy}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: '#9ca3af'
                }}>
                  Aucune contribution validée pour le moment
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'config' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{ fontSize: '18px', color: '#111827', margin: 0 }}>
              ⚙️ Configuration des Points
            </h2>
            {isAdmin && (
              <div style={{
                fontSize: '13px',
                color: '#6b7280',
                backgroundColor: '#f9fafb',
                padding: '6px 12px',
                borderRadius: '6px'
              }}>
                💡 Cliquez sur <Edit size={12} style={{ display: 'inline', marginLeft: '4px', marginRight: '4px' }} /> pour modifier
              </div>
            )}
          </div>
          <div style={{ padding: '20px' }}>
            {['essential', 'complementary', 'seo', 'avis'].map(category => {
              const categoryConfigs = pointsConfig.filter(c => c.category === category);
              if (categoryConfigs.length === 0) return null;

              return (
                <div key={category} style={{ marginBottom: '32px' }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: getCategoryColor(category),
                    marginBottom: '16px',
                    textTransform: 'capitalize'
                  }}>
                    {category === 'essential' && '⭐ Champs Essentiels'}
                    {category === 'complementary' && '📋 Champs Complémentaires'}
                    {category === 'seo' && '🔍 SEO & Infos Légales'}
                    {category === 'avis' && '💬 Avis Clients'}
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                    gap: '12px'
                  }}>
                    {categoryConfigs.map(config => (
                      <div
                        key={config._id}
                        style={{
                          padding: '16px',
                          backgroundColor: editingConfigId === config._id ? '#eff6ff' : '#f9fafb',
                          borderRadius: '8px',
                          border: editingConfigId === config._id ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                          position: 'relative',
                          transition: 'all 0.2s'
                        }}
                      >
                        {editingConfigId === config._id ? (
                          // Mode édition
                          <div>
                            <div style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#111827',
                              marginBottom: '12px'
                            }}>
                              {config.label}
                            </div>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              marginBottom: '8px'
                            }}>
                              <input
                                type="number"
                                min="0"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') saveEdit(config._id);
                                  if (e.key === 'Escape') cancelEdit();
                                }}
                                autoFocus
                                disabled={saving}
                                style={{
                                  flex: 1,
                                  padding: '8px 12px',
                                  border: '2px solid #3b82f6',
                                  borderRadius: '6px',
                                  fontSize: '16px',
                                  fontWeight: '600',
                                  outline: 'none'
                                }}
                              />
                              <span style={{
                                fontSize: '14px',
                                color: '#6b7280',
                                fontWeight: '600'
                              }}>
                                pts
                              </span>
                            </div>
                            <div style={{
                              display: 'flex',
                              gap: '8px'
                            }}>
                              <button
                                onClick={() => saveEdit(config._id)}
                                disabled={saving}
                                style={{
                                  flex: 1,
                                  padding: '8px',
                                  backgroundColor: '#10b981',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  cursor: saving ? 'not-allowed' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '6px',
                                  opacity: saving ? 0.6 : 1
                                }}
                              >
                                <Check size={14} />
                                {saving ? 'Enregistrement...' : 'Valider'}
                              </button>
                              <button
                                onClick={cancelEdit}
                                disabled={saving}
                                style={{
                                  padding: '8px 12px',
                                  backgroundColor: '#f3f4f6',
                                  color: '#6b7280',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  cursor: saving ? 'not-allowed' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <X size={14} />
                              </button>
                            </div>
                            {config.description && (
                              <div style={{
                                fontSize: '12px',
                                color: '#6b7280',
                                marginTop: '8px',
                                paddingTop: '8px',
                                borderTop: '1px solid #e5e7eb'
                              }}>
                                {config.description}
                              </div>
                            )}
                          </div>
                        ) : (
                          // Mode lecture
                          <div>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              marginBottom: '8px'
                            }}>
                              <div style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#111827',
                                flex: 1,
                                paddingRight: '8px'
                              }}>
                                {config.label}
                              </div>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <div style={{
                                  fontSize: '18px',
                                  fontWeight: '700',
                                  color: getCategoryColor(category)
                                }}>
                                  {config.points} pts
                                </div>
                                {isAdmin && (
                                  <button
                                    onClick={() => startEdit(config)}
                                    style={{
                                      padding: '6px',
                                      backgroundColor: 'white',
                                      border: '1px solid #e5e7eb',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#3b82f6';
                                      e.currentTarget.style.borderColor = '#3b82f6';
                                      e.currentTarget.querySelector('svg').style.color = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'white';
                                      e.currentTarget.style.borderColor = '#e5e7eb';
                                      e.currentTarget.querySelector('svg').style.color = '#6b7280';
                                    }}
                                    title="Modifier les points"
                                  >
                                    <Edit size={14} style={{ color: '#6b7280', transition: 'color 0.2s' }} />
                                  </button>
                                )}
                              </div>
                            </div>
                            {config.description && (
                              <div style={{
                                fontSize: '12px',
                                color: '#6b7280',
                                marginTop: '4px'
                              }}>
                                {config.description}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaderboardManager;